import React, { useCallback, useEffect, useState, useRef } from 'react';
import { FetchResult } from '@apollo/client';

import { SnapshotAction, track } from '../../global/tracking';
import { DiffStatus, UpdateDiffMutationMutation } from '../../graphql/__generated__/graphql';
import DecideButton, { DecideButtonTypes, decideCheckDuration } from '../common/DecideButton';
import KeyBind from '../common/KeyBind';
import { DiffViewMenu } from './components/DiffViewMenu';

export type DiffReviewButtonsProps = {
  diffStatus: DiffStatus;
  diffId: string;
  onAccept: () => Promise<FetchResult<UpdateDiffMutationMutation>>;
  onReject: () => Promise<FetchResult<UpdateDiffMutationMutation>>;
  onUnapprove: () => Promise<FetchResult<UpdateDiffMutationMutation>>;
  nextPage: () => void;
};

export const DiffReviewButtons: React.FC<DiffReviewButtonsProps> = ({
  diffStatus,
  diffId,
  onAccept,
  onReject,
  onUnapprove,
  nextPage,
}) => {
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const isAccepted = diffStatus === DiffStatus.Approved;
  const isRejected = diffStatus === DiffStatus.Rejected;
  const [isLoading, setIsLoading] = useState(false);
  const loadingWrapper = (fn: () => Promise<void>) => async () => {
    setIsLoading(true);
    await fn();
    setIsLoading(false);
  };
  const onClickAccept = () => {
    track.snapshotReviewed(SnapshotAction.Approve);
    void onAccept();
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(nextPage, decideCheckDuration);
  };

  const onClickReject = () => {
    track.snapshotReviewed(SnapshotAction.Reject);
    void onReject();
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(nextPage, decideCheckDuration);
  };

  const onClickUnapprove = useCallback(loadingWrapper(async () => {
    track.snapshotReviewed(SnapshotAction.Unapprove);
    await onUnapprove();
  }), [onUnapprove]);

  useEffect(() => () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  }, []);

  return (
    <>
      <div className="flex flex-col gap-2 text-gray-600 absolute top-2 right-2 z-20 items-end">
        <div
          className="flex shadow-md bg-white rounded"
          data-qa="diff-review-actions"
        >
          <KeyBind
            char={isAccepted ? 'U' : 'A'}
            func={isAccepted ? onClickUnapprove : onClickAccept}
            label={isAccepted ? 'Undo' : 'Accept'}
          >
            <DecideButton
              aria-label="Accept Snapshot"
              className="rounded-tr-none rounded-br-none"
              disabled={isLoading}
              id={diffId}
              isActive={isAccepted}
              isNoChanges={diffStatus === DiffStatus.Equal}
              onClick={onClickAccept}
              onUnapprove={onClickUnapprove}
              type={DecideButtonTypes.ACCEPT}
            />
          </KeyBind>
          <div className="h-auto border-0 border-r border-gray-200 border-solid" />
          <KeyBind
            char={isRejected ? 'U' : 'R'}
            func={isRejected ? onClickUnapprove : onClickReject}
            label={isRejected ? 'Undo' : 'Reject'}
          >
            <DecideButton
              aria-label="Reject Snapshot"
              className="rounded-bl-none rounded-tl-none"
              disabled={isLoading}
              id={diffId}
              isActive={isRejected}
              onClick={onClickReject}
              onUnapprove={onClickUnapprove}
              type={DecideButtonTypes.REJECT}
            />
          </KeyBind>
        </div>
        <DiffViewMenu />
      </div>
    </>
  );
};

export default DiffReviewButtons;
